// src/components/common/Header.js
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import MobileMenu from './MobileMenu';
import { colors } from '../../styles/variables';
import { useModal } from '../../contexts/ModalContext';

const HeaderContainer = styled.header`
  background-color: ${colors.white};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  padding: 0 24px;
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 64px;
`;

const Logo = styled(Link)`
  font-weight: 700;
  font-size: 24px;
  color: ${colors.primary};
  text-decoration: none;
  
  span {
    color: ${colors.secondary};
  }
`;

const Nav = styled.nav`
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavList = styled.ul`
  display: flex;
  list-style: none;
`;

const NavItem = styled.li`
  margin-left: 32px;
`;

const NavLink = styled(Link)`
  color: ${colors.primary};
  text-decoration: none;
  font-weight: 500;
  font-size: 16px;
  transition: color 0.2s;
  
  &:hover {
    color: ${colors.secondary};
  }
`;

const NavButton = styled.button`
  color: ${colors.primary};
  background: none;
  border: none;
  font-weight: 500;
  font-size: 16px;
  font-family: inherit;
  padding: 0;
  cursor: pointer;
  transition: color 0.2s;
  
  &:hover {
    color: ${colors.secondary};
  }
`;

const MainButton = styled.button`
  background-color: ${colors.secondary};
  color: ${colors.white};
  padding: 8px 16px;
  border-radius: 4px;
  font-weight: 500;
  text-decoration: none;
  border: none;
  font-family: inherit;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #00a759;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileMenuButton = styled.div`
  display: none;
  flex-direction: column;
  justify-content: space-between;
  width: 24px;
  height: 18px;
  cursor: pointer;
  
  @media (max-width: 768px) {
    display: flex;
  }
  
  span {
    display: block;
    height: 2px;
    width: 100%;
    background-color: ${colors.primary};
    transition: all 0.3s;
    
    &:nth-child(1) {
      transform: ${props => props.isOpen ? 'translateY(8px) rotate(45deg)' : 'none'};
    }
    
    &:nth-child(2) {
      opacity: ${props => props.isOpen ? 0 : 1};
    }
    
    &:nth-child(3) {
      transform: ${props => props.isOpen ? 'translateY(-8px) rotate(-45deg)' : 'none'};
    }
  }
`;

const Dropdown = styled.ul`
  display: ${({ open }) => (open ? 'block' : 'none')};
  position: absolute;
  top: 100%;
  left: 0;
  background: #fff;
  min-width: 180px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  z-index: 100;
  padding: 0.5rem 0;
  border-radius: 8px;
  @media (max-width: 768px) {
    display: none;
  }
`;

const DropdownItem = styled.li`
  padding: 0.5rem 1.5rem;
  white-space: nowrap;
  &:hover {
    background: #f0f0f0;
  }
`;

const NavItemWithDropdown = styled.li`
  position: relative;
  margin-left: 32px;
`;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const { openConsultationModal, openMentorModal } = useModal();
  const location = useLocation();
  
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleLogoClick = () => {
    window.scrollTo(0, 0);
  };

  const handleSectionScroll = (sectionId) => {
    if (location.pathname !== '/') {
      window.location.href = '/#benefits';
      return;
    }

    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "auto"
      });
    }
  };
  
  return (
    <HeaderContainer role="banner">
      <HeaderContent>
        <Logo to="/" onClick={handleLogoClick} title="ElectroWave Club 홈">Electro<span>Wave</span> Club</Logo>

        <MobileMenuButton
          isOpen={isMenuOpen}
          onClick={toggleMenu}
          aria-label="메뉴 열기"
        >
          <span></span>
          <span></span>
          <span></span>
        </MobileMenuButton>

        <Nav aria-label="주요 메뉴">
          <NavList>
            <NavItemWithDropdown
              onMouseEnter={() => setOpenDropdown('curriculum')}
              onMouseLeave={() => setOpenDropdown(null)}
            >
              <NavButton onClick={() => handleSectionScroll('benefits')}>
                커리큘럼
              </NavButton>
              <Dropdown open={openDropdown === 'curriculum'}>
                <DropdownItem><NavLink to="/java">Java</NavLink></DropdownItem>
                <DropdownItem><NavLink to="/java-advanced">Java 심화</NavLink></DropdownItem>
                <DropdownItem><NavLink to="/c">C 입문</NavLink></DropdownItem>
                <DropdownItem><NavLink to="/database">Database</NavLink></DropdownItem>
                <DropdownItem><NavLink to="/react">React</NavLink></DropdownItem>
                <DropdownItem><NavLink to="/typescript">TypeScript</NavLink></DropdownItem>
                <DropdownItem><NavLink to="/ai-coding">AI 코딩</NavLink></DropdownItem>
              </Dropdown>
            </NavItemWithDropdown>
            <NavItem>
              <NavLink to="/tuition">회비 안내</NavLink>
            </NavItem>
            <NavItem>
              <NavLink to="/instructors">강사진</NavLink>
            </NavItem>
            <NavItem>
              <NavLink to="/reviews">수강생 후기</NavLink>
            </NavItem>
            <NavItemWithDropdown
              onMouseEnter={() => setOpenDropdown('about')}
              onMouseLeave={() => setOpenDropdown(null)}
            >
              <NavLink to="/about">클럽 소개</NavLink>
              <Dropdown open={openDropdown === 'about'}>
                <DropdownItem><NavLink to="/about">클럽 소개</NavLink></DropdownItem>
                <DropdownItem><NavLink to="/learning-guide">학습 가이드</NavLink></DropdownItem>
                <DropdownItem><NavLink to="/faq">자주 묻는 질문</NavLink></DropdownItem>
                <DropdownItem><NavLink to="/refund-policy">환불 정책</NavLink></DropdownItem>
              </Dropdown>
            </NavItemWithDropdown>
            <NavItem>
              <NavButton onClick={openConsultationModal}>교육상담</NavButton>
            </NavItem>
          </NavList>
        </Nav>
        
        <MainButton onClick={openMentorModal}>멘토 신청</MainButton>
      </HeaderContent>
      
      <MobileMenu 
        isOpen={isMenuOpen} 
        closeMenu={() => setIsMenuOpen(false)}
        openConsultationModal={openConsultationModal}
        openMentorModal={openMentorModal}
      />
    </HeaderContainer>
  );
};

export default Header;